
import Vue from "vue";
import { mapGetters, mapMutations, mapActions } from "vuex";
import {
  GET_USER_DETAILS,
  USER_DETAILS_BY_ID,
  USER_PROFILE_BY_ID
} from "@/store/modules/auth/constants";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import RecruiterAiCandidateCard from "@/components/recruiter/interview/ai/RecruiterAiCandidateCard.vue";
import RecruiterAiInterviewDetailsCard from "@/components/recruiter/interview/ai/RecruiterAiInterviewDetailsCard.vue";
import RecruiterCandidateInformationDialog from "@/components/recruiter/interview/ai/RecruiterCandidateInformationDialog.vue";
import {
  AI_INTERVIEW_INVITE_BY_ID,
  INIT_AI_INTERVIEW_INVITES,
  SCHEDULE_AI_INTERVIEW_INVITE
} from "@/store/modules/recruiter_ai_interviews/constants";
import {
  generate_ai_report,
  get_latest_interview_report,
  upsert_interview_report
} from "@/store/apis/ml_interview";
import { get_jobs_posted_by_recruiter } from "@/store/apis/recruiter";

export default Vue.extend({
  name: "RecruiterAiInterviewDetails",
  components: {
    RecruiterAiCandidateCard,
    RecruiterAiInterviewDetailsCard,
    RecruiterCandidateInformationDialog
  },
  data() {
    return {
      coverImage: require("@/assets/images/interviews/ai_interview_cover.png"),
      fallbackAvatar: require("@/assets/images/female-bot.png"),
      showCandidateInfo: false,
      candidateUser: null as any,
      candidateProfile: null as any,
      reportRecord: null as any,
      generatingAiReport: false,
      resolvedJobTitle: "",
      resolvedJobDescription: "",
      showRescheduleDialog: false,
      rescheduling: false,
      rescheduleForm: {
        scheduled_at: "",
        duration_minutes: 30
      }
    };
  },
  computed: {
    ...mapGetters("auth", {
      user: GET_USER_DETAILS
    }),
    ...mapGetters("recruiter_ai_interviews", {
      inviteById: AI_INTERVIEW_INVITE_BY_ID
    }),
    invite(this: any): any {
      const id = (this.$route.query.inviteId as string) || "";
      if (!id) return null;
      return (this as any).inviteById(id);
    },
    candidateName(this: any): string {
      if (this.candidateProfile?.profile?.personal_details?.name) {
        const raw = Object.values(this.candidateProfile.profile.personal_details.name)
          .filter((v: any) => v)
          .join(" ");
        return this.normalizeName(raw);
      }
      return this.normalizeName(this.invite?.candidate_name || "AI Interview Candidate");
    },
    candidateRole(this: any): string {
      const profile = this.candidateProfile?.profile || {};
      const workHistory = profile?.work_history_detailed || {};
      const roleFromWorkHistory = (Object.values(workHistory) as any[])
        .map((item: any) => String(item?.job_title || "").trim())
        .find((value: string) => !!value);
      if (roleFromWorkHistory) return roleFromWorkHistory;
      const roleFromPersonalDetails = String(
        profile?.personal_details?.profession ||
          profile?.personal_details?.designation ||
          ""
      ).trim();
      if (roleFromPersonalDetails) return roleFromPersonalDetails;
      return this.invite?.candidate_role || "Candidate";
    },
    inviteRole(this: any): string {
      const inviteJobRole = String(this.invite?.job_role || "").trim();
      const safeInviteJobRole = /^job\s*#\s*\d+$/i.test(inviteJobRole)
        ? ""
        : inviteJobRole;
      const storeSelectedJobTitle = String(
        this.$store?.state?.recruiter?.view_candidates_dynamically?.job_title || ""
      ).trim();
      const postedJobs = this.$store?.state?.recruiter?.jobs_posted?.results || [];
      const matchedJob = postedJobs.find(
        (job: any) => Number(job?.jid || 0) === Number(this.invite?.job_id || 0)
      );
      const matchedJobTitle = String(matchedJob?.job_title || "").trim();

      // Prefer real job titles first, then keep invite role text (even "Job #123")
      // but never show job-id reflection such as "Job #12422".
      const roleCandidates = [
        this.resolvedJobTitle,
        this.invite?.job_title,
        storeSelectedJobTitle,
        matchedJobTitle,
        safeInviteJobRole,
        this.invite?.candidate_role,
        this.candidateRole
      ]
        .map((v: any) => String(v || "").trim())
        .filter((v: string) => !!v);
      return roleCandidates[0] || "Candidate";
    },
    durationDisplay(this: any): string {
      const duration = Number(this.invite?.duration_minutes);
      return Number.isFinite(duration) && duration > 0
        ? `${duration} Minutes`
        : "30 Minutes";
    },
    avatarUri(this: any): string {
      return (
        this.candidateUser?.avatar_uri ||
        this.invite?.avatar_uri ||
        this.user?.avatar_uri ||
        this.fallbackAvatar
      );
    },
    reportReady(this: any): boolean {
      return !!(this.reportRecord?.ai_report && Object.keys(this.reportRecord.ai_report).length);
    },
    reportStatusText(this: any): string {
      if (this.reportReady) {
        const generatedAt = this.reportRecord?.generated_at;
        if (!generatedAt) return "AI interview report is ready.";
        return `AI interview report generated on ${new Date(generatedAt).toLocaleString()}.`;
      }
      if (
        this.reportRecord &&
        Array.isArray(this.reportRecord?.ai_transcript) &&
        this.reportRecord.ai_transcript.length > 0
      ) {
        return "AI interview completed. Generate report to view result.";
      }
      return "AI interview transcript is not available yet.";
    },
    canGenerateAiReport(this: any): boolean {
      return (
        !this.generatingAiReport &&
        !this.reportReady &&
        !!this.reportRecord &&
        Array.isArray(this.reportRecord?.ai_transcript) &&
        this.reportRecord.ai_transcript.length > 0
      );
    },
    aiPrimaryActionLabel(this: any): string {
      if (this.generatingAiReport) return "Generating AI Report...";
      return this.reportReady ? "View AI Interview Result" : "Generate AI Report";
    },
    aiPrimaryActionDisabled(this: any): boolean {
      if (this.generatingAiReport) return true;
      if (this.reportReady) return false;
      return !this.canGenerateAiReport;
    },
    canReschedule(this: any): boolean {
      if (!this.invite) return false;
      const status = String(this.invite?.status || "").toLowerCase();
      if (status !== "scheduled") return false;
      const scheduledAt = String(this.invite?.scheduled_at || "").trim();
      const ts = new Date(scheduledAt).getTime();
      if (!Number.isFinite(ts)) return false;
      return Date.now() > ts;
    }
  },
  async created(this: any) {
    await this.initInvites();
    await this.loadCandidateData();
  },
  watch: {
    "$route.query.inviteId": {
      immediate: false,
      async handler(this: any) {
        await this.loadCandidateData();
      }
    },
    invite: {
      immediate: false,
      async handler(this: any) {
        await this.loadCandidateData();
      }
    }
  },
  methods: {
    ...mapActions("auth", {
      fetchUserById: USER_DETAILS_BY_ID,
      fetchProfileById: USER_PROFILE_BY_ID
    }),
    ...mapActions("recruiter_ai_interviews", {
      initInvites: INIT_AI_INTERVIEW_INVITES,
      scheduleInvite: SCHEDULE_AI_INTERVIEW_INVITE
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION
    }),
    notify(this: any, message: string) {
      this.set_root_notification(message);
    },
    normalizeName(this: any, name: string) {
      const cleaned = String(name || "").replace(/\s+/g, " ").trim();
      if (!cleaned) return "Candidate";
      const parts = cleaned.split(" ");
      const collapsed = parts.filter(
        (p, i) => i === 0 || p.toLowerCase() !== parts[i - 1].toLowerCase()
      );
      const half = Math.floor(collapsed.length / 2);
      if (
        collapsed.length % 2 === 0 &&
        half > 0 &&
        collapsed.slice(0, half).join(" ").toLowerCase() ===
          collapsed.slice(half).join(" ").toLowerCase()
      ) {
        return collapsed.slice(0, half).join(" ");
      }
      return collapsed.join(" ");
    },
    async onResumeClick(this: any) {
      if (!this.candidateUser?.resume_uri) {
        this.notify("Resume is not available for this candidate.");
        return;
      }
      window.open(this.candidateUser.resume_uri, "_blank");
    },
    onCandidateInfoClick(this: any) {
      this.showCandidateInfo = true;
    },
    openRescheduleDialog(this: any) {
      if (!this.invite) return;
      const d = new Date(String(this.invite?.scheduled_at || ""));
      this.rescheduleForm = {
        scheduled_at: Number.isFinite(d.getTime())
          ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
          : "",
        duration_minutes: Number(this.invite?.duration_minutes || 30) || 30
      };
      this.showRescheduleDialog = true;
    },
    async submitReschedule(this: any) {
      if (!this.invite || !this.rescheduleForm.scheduled_at || !this.rescheduleForm.duration_minutes) return;
      this.rescheduling = true;
      try {
        await this.scheduleInvite({
          interview_type: "ai",
          job_id: Number(this.invite?.job_id || 0) || null,
          job_role: this.invite?.job_role || null,
          candidate_id: Number(this.invite?.candidate_id || 0),
          upload_response_id: this.invite?.upload_response_id || null,
          candidate_name: this.invite?.candidate_name || "Candidate",
          candidate_role: this.invite?.candidate_role || "Candidate",
          avatar_uri: this.invite?.avatar_uri || null,
          duration_minutes: Number(this.rescheduleForm.duration_minutes),
          scheduled_at: new Date(this.rescheduleForm.scheduled_at).toISOString()
        });
        await this.initInvites();
        await this.loadCandidateData();
        this.showRescheduleDialog = false;
        this.notify("Interview rescheduled.");
      } finally {
        this.rescheduling = false;
      }
    },
    async loadCandidateData(this: any) {
      const candidateId = this.invite?.candidate_id;
      if (!candidateId) return;
      const [user, profile] = await Promise.all([
        this.fetchUserById(candidateId),
        this.fetchProfileById(candidateId)
      ]);
      this.candidateUser = user;
      this.candidateProfile = profile;
      await this.resolveInviteJobTitle();
      await this.loadLatestAiReport();
    },
    async resolveInviteJobTitle(this: any) {
      this.resolvedJobTitle = "";
      this.resolvedJobDescription = "";
      const jobId = Number(this.invite?.job_id || 0);
      if (!jobId) return;
      try {
        // Fetch paginated jobs until the exact job_id is found.
        const limit = 40;
        let page = 0;
        let total = Number.MAX_SAFE_INTEGER;
        while (page * limit < total && page < 50) {
          const resp = await get_jobs_posted_by_recruiter({
            page,
            limit_per_page: limit,
            dashboard: false
          });
          const payload = resp?.data?.data || {};
          const jobs = payload?.results || [];
          const job = jobs.find((j: any) => Number(j?.jid || 0) === jobId);
          if (job) {
            this.resolvedJobTitle = String(job?.job_title || "").trim();
            this.resolvedJobDescription = String(
              job?.job_description || job?.description || ""
            ).trim();
            return;
          }
          total = Number(payload?.total || 0);
          page += 1;
        }
      } catch (_e) {
        this.resolvedJobTitle = "";
        this.resolvedJobDescription = "";
      }
    },
    async loadLatestAiReport(this: any) {
      const candidateId = this.invite?.candidate_id;
      const jobId = Number(this.invite?.job_id || 0) || null;
      if (!candidateId) {
        this.reportRecord = null;
        return;
      }
      try {
        const resp = await get_latest_interview_report(Number(candidateId), "ai", jobId);
        this.reportRecord = resp?.data?.data || null;
      } catch (e) {
        this.reportRecord = null;
      }
    },
    async generateAiReportNow(this: any) {
      if (!this.canGenerateAiReport) return;
      const candidateId = Number(this.invite?.candidate_id || 0);
      const jobId = Number(this.invite?.job_id || 0) || null;
      if (!candidateId) return;
      this.generatingAiReport = true;
      try {
        const latestResp = await get_latest_interview_report(candidateId, "ai", jobId);
        const latest = latestResp?.data?.data || {};
        const transcript = Array.isArray(latest?.ai_transcript) ? latest.ai_transcript : [];
        if (!transcript.length) {
          this.notify("AI transcript is not available yet.");
          return;
        }
        const mlTranscript = transcript
          .map((item: any) => {
            const speakerRaw = String(item?.speaker || "").toLowerCase();
            const roleRaw = String(item?.role || "").toLowerCase();
            const text = String(item?.text || item?.content || "").trim();
            if (!text) return null;
            const speaker =
              speakerRaw === "interviewer" || speakerRaw === "candidate"
                ? speakerRaw
                : roleRaw === "assistant"
                  ? "interviewer"
                  : "candidate";
            return { speaker, text };
          })
          .filter((item: any) => !!item);
        if (!mlTranscript.length) {
          this.notify("AI transcript is empty.");
          return;
        }
        const resumeText =
          JSON.stringify(this.candidateProfile?.profile || {}) || "Candidate resume";
        const jdText =
          this.resolvedJobDescription ||
          String(this.invite?.job_description || "").trim() ||
          this.inviteRole ||
          "Interview role";
        const reportResp = await generate_ai_report({
          resume_text: resumeText,
          jd_text: jdText,
          transcript: mlTranscript,
          question_bank: []
        });
        const aiReport =
          reportResp?.data?.data?.ai_report || reportResp?.data?.ai_report || {};
        if (!Object.keys(aiReport).length) {
          this.notify("AI report generation returned empty response.");
          return;
        }
        const answeredQuestions = mlTranscript.filter(
          (item: any) => item?.speaker === "candidate"
        ).length;
        // Fallback estimate when runtime timer metadata is unavailable on recruiter-side generation.
        const estimatedTimeSpentSeconds = Math.max(0, answeredQuestions * 45);
        const aiReportWithMeta = {
          ...aiReport,
          _hcms_meta: {
            answered_questions: answeredQuestions,
            total_questions: 10,
            time_spent_seconds: estimatedTimeSpentSeconds
          }
        };
        await upsert_interview_report({
          candidate_id: candidateId,
          interview_type: "ai",
          job_id: jobId,
          thread_id: latest?.thread_id || null,
          ai_transcript: mlTranscript,
          question_bank: latest?.question_bank || [],
          ai_report: aiReportWithMeta,
          status: "generated"
        });
        await this.loadLatestAiReport();
      } finally {
        this.generatingAiReport = false;
      }
    },
    goToInterviewReport(this: any) {
      if (!this.reportReady) {
        this.notify("AI interview report is not generated yet.");
        return;
      }
      this.$router.push({
        name: "recruiter-ai-interview-report",
        query: {
          candidateId: String(this.invite?.candidate_id || ""),
          jobId: String(this.invite?.job_id || "")
        }
      });
    },
    async onAiPrimaryAction(this: any) {
      if (this.reportReady) {
        this.goToInterviewReport();
        return;
      }
      await this.generateAiReportNow();
    }
  }
});
